/* eslint-disable class-methods-use-this */
// TODO: Extract languages and translations into @gms/i18n library
// import { DEFAULT_LANGUAGE } from '@gms/language/src/model/languages';
const DEFAULT_LANGUAGE = 'en';
import Axios, { AxiosRequestConfig } from 'axios';
import Response from './response';
import RestService from './restService';

class AxiosRestService implements RestService {
  private unauthorizedResponseHandlers: Array<() => void> = [];

  private forbiddenResponseHandlers: Array<() => void> = [];

  private interruptedRequestResponseHandlers: Array<() => void> = [];

  private responseErrorHandlers: Array<() => void> = [];

  // eslint-disable-next-line no-useless-constructor
  constructor() {
    Axios.defaults.headers.common = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    };

    Axios.defaults.withCredentials = true;

    Axios.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        // If the client received a valid response, handle special error cases
        if (error.response && error.response.data !== undefined) {
          switch (error.response.status) {
            case 401:
              this.unauthorizedResponseHandlers.forEach((handler) => handler());
              break;
            case 403:
              this.forbiddenResponseHandlers.forEach((handler) => handler());
              break;
            case 500:
              this.responseErrorHandlers.forEach((handler) => handler());
              break;
            default:
              break;
          }
        // If no response was received, but a request was sent, we expect
        // the client went offline or the response was lost in transmission,
        // and we show a specific error message for this case
        } else if (error.request) {
          // The request could not be issued successfully
          this.interruptedRequestResponseHandlers.forEach((handler) => handler());
        // All other cases should show the default error message
        } else {
          this.responseErrorHandlers.forEach((handler) => handler());
        }

        return Promise.reject(error);
      },
    );
  }

  addUnauthorizedResponseHandler(handler: () => void): void {
    this.unauthorizedResponseHandlers.push(handler);
  }

  addForbiddenResponseHandler(handler: () => void): void {
    this.forbiddenResponseHandlers.push(handler);
  }

  addInterruptedRequestResponseHandler(handler: () => void) {
    this.interruptedRequestResponseHandlers.push(handler);
  }

  addResponseErrorHandler(handler: () => void): void {
    this.responseErrorHandlers.push(handler);
  }

  setBaseUrl(url: string) {
    Axios.defaults.baseURL = url;
  }

  setAcceptedLanguage(language: string | number | boolean | undefined): void {
    if (language && typeof language === 'string') {
      Axios.defaults.headers.common['Accept-Language'] = language;
    } else {
      Axios.defaults.headers.common['Accept-Language'] = DEFAULT_LANGUAGE;
    }
  }

  resetAcceptedLanguage(): string | number | boolean | undefined {
    const value = Axios.defaults.headers.common['Accept-Language'];
    Axios.defaults.headers.common['Accept-Language'] = DEFAULT_LANGUAGE;
    return value;
  }

  setContentLanguage(language: string | number | boolean | undefined): void {
    if (language && typeof language === 'string') {
      Axios.defaults.headers.common['Content-Language'] = language;
    } else {
      Axios.defaults.headers.common['Content-Language'] = DEFAULT_LANGUAGE;
    }
  }

  resetContentLanguage(): string | number | boolean | undefined {
    const value = Axios.defaults.headers.common['Content-Language'];
    delete Axios.defaults.headers.common['Content-Language'];
    return value;
  }

  setAuthorizationHeader(value: string): void {
    Axios.defaults.headers.common.Authorization = value;
  }

  unsetAuthorizationHeader(): void {
    delete Axios.defaults.headers.common.Authorization;
  }

  setClientId(value: string): void {
    Axios.defaults.headers.common['X-Client-Id'] = value
  }

  unsetClientId() {
    delete Axios.defaults.headers.common['X-Client-Id']
  }

  setBearerToken(token: string): void {
    Axios.defaults.headers.common.Authorization = `bearer ${token}`;
  }

  static ensureDefaultAcceptedLanguageHeaderIsNotUndefined(): void {
    const h = Axios.defaults?.headers?.common['Accept-Language'];
    if (h === 'undefined' || h === 'null' || h === '' || h === undefined) {
      delete Axios.defaults.headers.common['Accept-Language'];
    }
  }

  get<T>(url: string, config?: AxiosRequestConfig): Promise<Response<T>> {
    AxiosRestService.ensureDefaultAcceptedLanguageHeaderIsNotUndefined();
    return Axios.get(url, config);
  }

  post<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<Response<T>> {
    AxiosRestService.ensureDefaultAcceptedLanguageHeaderIsNotUndefined();
    return Axios.post(url, data, config);
  }

  patch<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<Response<T>> {
    AxiosRestService.ensureDefaultAcceptedLanguageHeaderIsNotUndefined();
    return Axios.patch(url, data, config);
  }

  put<T>(url: string, data: unknown, config?: AxiosRequestConfig): Promise<Response<T>> {
    AxiosRestService.ensureDefaultAcceptedLanguageHeaderIsNotUndefined();
    return Axios.put(url, data, config);
  }

  delete<T>(url: string, config?: AxiosRequestConfig): Promise<Response<T>> {
    AxiosRestService.ensureDefaultAcceptedLanguageHeaderIsNotUndefined();
    return Axios.delete(url, config);
  }
}

export default new AxiosRestService() as RestService;
